/**
 * A parked send, wherever it is shown.
 *
 * The chat bubble, the butler card and /assistant/confirm render this one
 * component, so a merchant meets the same card with the same two buttons in
 * every place the assistant can park something. The card owns its own
 * outcome: once decided it says what happened and offers nothing further,
 * because a second press on a released send is exactly the double-send the
 * single-use claim exists to refuse.
 */
import { Fragment, useState } from 'react';
import { t } from '../../i18n';
import { decidePending, isExpired, type PendingDecision } from './pendingDecision';

export interface PendingCardAction {
  readonly id: string;
  readonly toolName: string;
  readonly expiresAt: string;
  readonly confirmHash: string;
  /** Present only when a host deep link carried the single-use token. */
  readonly token?: string | null;
  readonly inputSummary?: Readonly<Record<string, string | number | boolean | null>>;
}

type CardState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'settled'; readonly decision: PendingDecision };

const SUMMARY_ROWS = 5;

function minutesLeft(expiresAt: string, now: number = Date.now()): number {
  return Math.max(0, Math.floor((Date.parse(expiresAt) - now) / 60_000));
}

/**
 * The words on the card. Most parked tools are sends, and "Send it" fits them;
 * switching a popup on is not a send, and the card used to ask it with the
 * raw popupId and "Send it" (dogfood V34). The server adds `popupName` to the
 * summary for these rows (pendingConfirmations.popupNamesFor).
 */
interface CardCopy {
  readonly heading: string;
  readonly explain: string;
  /** Null when the action would be refused: the card then offers only Cancel. */
  readonly confirm: string | null;
  readonly showSummary: boolean;
  readonly done: string;
  readonly cancelled: string;
}

/** A non-blank string from the summary, or null. */
function textOf(summary: PendingCardAction['inputSummary'], key: string): string | null {
  const value = summary?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** send_campaign: the server adds campaignName and recipientCount (pendingConfirmations). */
function campaignHeading(summary: PendingCardAction['inputSummary']): string {
  const name = textOf(summary, 'campaignName');
  if (name === null) return t('assistant.pending.campaignSendUnnamed');
  const count = summary?.recipientCount;
  if (typeof count !== 'number') return t('assistant.pending.campaignSendNamed', { name });
  return count === 1
    ? t('assistant.pending.campaignSendOne', { name })
    : t('assistant.pending.campaignSendCount', { name, count: count.toLocaleString() });
}

/**
 * send_campaign refuses a campaign with no segment (EMPTY_AUDIENCE); the
 * server marks those rows `audienceEmpty` (V73). Never "Send to N" for them.
 */
function campaignNoAudience(summary: PendingCardAction['inputSummary']): CardCopy {
  const name = textOf(summary, 'campaignName');
  return {
    heading: name !== null
      ? t('assistant.pending.campaignNoAudience', { name })
      : t('assistant.pending.campaignNoAudienceUnnamed'),
    explain: t('assistant.pending.campaignNoAudienceExplain'),
    confirm: null,
    showSummary: false,
    done: t('assistant.pending.confirmed'),
    cancelled: t('assistant.pending.cancelled'),
  };
}

export function cardCopy(action: Pick<PendingCardAction, 'toolName' | 'inputSummary'>): CardCopy {
  if (action.toolName === 'send_campaign' && action.inputSummary?.audienceEmpty === true) {
    return campaignNoAudience(action.inputSummary);
  }
  if (action.toolName === 'send_campaign') {
    return {
      heading: campaignHeading(action.inputSummary),
      explain: t('assistant.pending.campaignSendExplain'),
      confirm: t('assistant.pending.campaignSendConfirm'),
      showSummary: false,
      done: t('assistant.pending.confirmed'),
      cancelled: t('assistant.pending.cancelled'),
    };
  }
  if (action.toolName === 'activate_popup') {
    const name = textOf(action.inputSummary, 'popupName');
    return {
      heading: name !== null
        ? t('assistant.pending.popupLive', { name })
        : t('assistant.pending.popupLiveUnnamed'),
      explain: t('assistant.pending.popupLiveExplain'),
      confirm: t('assistant.pending.popupLiveConfirm'),
      showSummary: false,
      done: t('assistant.pending.popupLiveDone'),
      cancelled: t('assistant.pending.popupLiveCancelled'),
    };
  }
  return {
    heading: t('assistant.pending.generic', { tool: action.toolName.replace(/_/g, ' ') }),
    explain: t('assistant.pending.genericExplain'),
    confirm: t('assistant.pending.genericConfirm'),
    showSummary: true,
    done: t('assistant.pending.genericDone'),
    cancelled: t('assistant.pending.genericCancelled'),
  };
}

function resultText(decision: PendingDecision, copy: CardCopy): string {
  switch (decision.outcome) {
    case 'sent':
      return copy.done;
    case 'cancelled':
      return copy.cancelled;
    case 'expired':
      return t('assistant.pending.expired');
    default:
      return decision.message || t('assistant.pending.failed');
  }
}

export function PendingActionCard({
  action,
  onSettled,
}: {
  action: PendingCardAction;
  onSettled?: (decision: PendingDecision) => void;
}): JSX.Element {
  const [state, setState] = useState<CardState>(() =>
    isExpired(action.expiresAt)
      ? { kind: 'settled', decision: { outcome: 'expired' } }
      : { kind: 'idle' },
  );

  const decide = (confirm: boolean): void => {
    setState({ kind: 'busy' });
    void decidePending(
      action.id,
      { confirmHash: action.confirmHash, token: action.token },
      confirm,
      action.expiresAt,
    ).then((decision) => {
      setState({ kind: 'settled', decision });
      onSettled?.(decision);
    });
  };

  const copy = cardCopy(action);
  const summary = copy.showSummary ? Object.entries(action.inputSummary ?? {}).slice(0, SUMMARY_ROWS) : [];
  const minutes = minutesLeft(action.expiresAt);

  return (
    <div
      data-testid="pending-action-card"
      className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
    >
      <p className="font-medium">{copy.heading}</p>
      <p className="mt-1 text-xs leading-relaxed opacity-80">{copy.explain}</p>

      {summary.length > 0 && (
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs opacity-90">
          {summary.map(([key, value]) => (
            <Fragment key={key}>
              <dt className="font-mono">{key}</dt>
              <dd className="truncate">{value === null ? '' : String(value)}</dd>
            </Fragment>
          ))}
        </dl>
      )}

      {state.kind === 'settled' ? (
        <p role="status" className="mt-3 text-xs font-medium">
          {resultText(state.decision, copy)}
        </p>
      ) : (
        <>
          <p className="mt-2 text-xs opacity-70">
            {minutes >= 1
              ? t('assistant.pending.expiresIn', { minutes })
              : t('assistant.pending.expiresSoon')}
          </p>
          <div className="mt-3 flex gap-2">
            {copy.confirm !== null && (
              <button
                type="button"
                disabled={state.kind === 'busy'}
                onClick={() => decide(true)}
                className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-60"
              >
                {state.kind === 'busy' ? t('assistant.pending.busy') : copy.confirm}
              </button>
            )}
            <button
              type="button"
              disabled={state.kind === 'busy'}
              onClick={() => decide(false)}
              className="rounded-md px-3 py-1.5 text-xs opacity-80 hover:opacity-100 disabled:opacity-60"
            >
              {t('assistant.pending.cancel')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
